import { api } from '../client';
import type { CertificateTestResult } from '../../types';

// Types
export type CertificateType =
  | 'MILL_TEST_CERTIFICATE'
  | 'CERTIFICATE_OF_CONFORMANCE'
  | 'API_Q1'
  | 'API_Q2'
  | 'NACE_MR0175'
  | 'ISO_CERTIFICATION'
  | 'MTR'
  | 'COC'
  | 'COA'
  | 'CALIBRATION'
  | 'SAFETY'
  | 'ENVIRONMENTAL'
  | 'QUALITY'
  | 'OTHER';

export type CertificateStatus =
  'VALID' | 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'PENDING_VERIFICATION' | 'PENDING_RENEWAL';
export type ExpiryStatus = 'VALID' | 'EXPIRING_SOON' | 'EXPIRED' | 'DISPOSED';

export interface CertificateItem {
  id: string;
  certificateId: string;
  itemId?: string;
  item?: {
    id: string;
    name: string;
    sku: string;
  };
  equipmentId?: string;
  equipment?: {
    id: string;
    assetNumber: string;
    name: string;
  };
  batchNumber?: string;
  serialNumber?: string;
  quantity?: number;
  heatNumber?: string;
  lotNumber?: string;
  notes?: string;
  createdAt: string;
}

export interface Certificate {
  id: string;
  companyId: string;
  certificateNumber: string;
  type: CertificateType;
  subType?: string;
  name?: string;
  issuedBy?: string;
  issuedDate: string;
  expiryDate?: string;
  issuingAuthority?: string;
  manufacturerName?: string;
  documentUrl?: string;
  documentName?: string;
  documentSize?: number;
  documentMimeType?: string;
  heatNumber?: string;
  lotNumber?: string;
  complianceStandards?: string[];
  testResults?: CertificateTestResult[];
  status: CertificateStatus;
  verifiedById?: string;
  verifiedAt?: string;
  uploadedById?: string;
  uploadedBy?: {
    id: string;
    fullName: string;
    email: string;
  };
  verifiedBy?: {
    id: string;
    fullName: string;
    email: string;
  };
  notes?: string;
  items?: CertificateItem[];
  _count?: {
    items: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface MaterialExpiry {
  id: string;
  companyId: string;
  itemId: string;
  item: {
    id: string;
    name: string;
    sku: string;
  };
  batchNumber?: string;
  expiryDate: string;
  quantity: number;
  locationId: string;
  location: {
    id: string;
    name: string;
    code: string;
  };
  alertSent: boolean;
  status: ExpiryStatus;
  daysUntilExpiry?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CertificatesDashboard {
  certificates: {
    total: number;
    /** Held and good. Overlaps `expiring` and part of `expired` — see `buckets`. */
    active: number;
    expiring: number;
    expired: number;
    /** Four mutually exclusive buckets summing to `total`. Use these to draw a distribution. */
    buckets: {
      inDate: number;
      expiringSoon: number;
      expired: number;
      unverified: number;
    };
  };
  materialExpiry: {
    total: number;
    expiring: number;
    expired: number;
  };
  recentCertificates: Certificate[];
}

export interface QueryCertificateParams {
  type?: CertificateType;
  status?: CertificateStatus;
  search?: string;
  expiryBefore?: string;
  expiryAfter?: string;
  page?: number;
  limit?: number;
}

export interface QueryMaterialExpiryParams {
  itemId?: string;
  locationId?: string;
  status?: ExpiryStatus;
  daysUntilExpiry?: number;
  page?: number;
  limit?: number;
}

export interface CreateCertificateDto {
  type: CertificateType;
  name: string;
  issuedBy: string;
  issuedDate: string;
  expiryDate?: string;
  documentUrl?: string;
  notes?: string;
}

export interface UpdateCertificateDto extends Partial<CreateCertificateDto> {
  status?: CertificateStatus;
}

export interface LinkItemDto {
  itemId?: string;
  equipmentId?: string;
  batchNumber?: string;
  serialNumber?: string;
}

export interface CreateMaterialExpiryDto {
  itemId: string;
  batchNumber?: string;
  expiryDate: string;
  locationId: string;
}

export interface UpdateMaterialExpiryDto {
  expiryDate?: string;
  status?: ExpiryStatus;
}

// API Functions
export const certificatesApi = {
  // Certificates
  getAll: async (params?: QueryCertificateParams) => {
    const response = await api.get<{
      data: Certificate[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>('/certificates', { params });
    return response.data;
  },

  getDashboard: async () => {
    const response = await api.get<CertificatesDashboard>('/certificates/dashboard');
    return response.data;
  },

  getExpiring: async (days?: number) => {
    const response = await api.get<Certificate[]>('/certificates/expiring', {
      params: days ? { days } : undefined,
    });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<Certificate>(`/certificates/${id}`);
    return response.data;
  },

  create: async (data: CreateCertificateDto) => {
    const response = await api.post<Certificate>('/certificates', data);
    return response.data;
  },

  update: async (id: string, data: UpdateCertificateDto) => {
    const response = await api.patch<Certificate>(`/certificates/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/certificates/${id}`);
    return response.data;
  },

  linkItem: async (certificateId: string, data: LinkItemDto) => {
    const response = await api.post<CertificateItem>(`/certificates/${certificateId}/items`, data);
    return response.data;
  },

  unlinkItem: async (certificateId: string, itemId: string) => {
    const response = await api.delete(`/certificates/${certificateId}/items/${itemId}`);
    return response.data;
  },

  // Material Expiry
  getMaterialExpiries: async (params?: QueryMaterialExpiryParams) => {
    const response = await api.get<{
      data: MaterialExpiry[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>('/certificates/material-expiry', { params });
    return response.data;
  },

  createMaterialExpiry: async (data: CreateMaterialExpiryDto) => {
    const response = await api.post<MaterialExpiry>('/certificates/material-expiry', data);
    return response.data;
  },

  updateMaterialExpiry: async (id: string, data: UpdateMaterialExpiryDto) => {
    const response = await api.patch<MaterialExpiry>(`/certificates/material-expiry/${id}`, data);
    return response.data;
  },

  // Material Certification - New Methods
  createWithFile: async (data: CreateCertificateDto, file?: File) => {
    const formData = new FormData();

    if (file) {
      formData.append('document', file);
    }

    // Append other fields
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value.toString());
        }
      }
    });

    const response = await api.post<Certificate>('/certificates', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  linkBatch: async (
    certificateId: string,
    data: {
      batchNumber: string;
      itemId: string;
      quantity: number;
      heatNumber?: string;
      lotNumber?: string;
      notes?: string;
    }
  ) => {
    const response = await api.post<CertificateItem>(
      `/certificates/${certificateId}/link-batch`,
      data
    );
    return response.data;
  },

  validateBatch: async (batchNumber: string) => {
    const response = await api.post<{
      valid: boolean;
      message: string;
      batchNumber: string;
      certificates?: Certificate[];
    }>('/certificates/validate-batch', { batchNumber });
    return response.data;
  },

  searchByHeatNumber: async (heatNumber: string) => {
    const response = await api.get<Certificate[]>(`/certificates/search/heat/${heatNumber}`);
    return response.data;
  },

  searchByLotNumber: async (lotNumber: string) => {
    const response = await api.get<Certificate[]>(`/certificates/search/lot/${lotNumber}`);
    return response.data;
  },

  downloadDocument: async (id: string) => {
    const response = await api.get(`/certificates/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  deleteDocument: async (id: string) => {
    const response = await api.delete(`/certificates/${id}/document`);
    return response.data;
  },
};

// Utility functions
export function formatCertificateType(type: CertificateType): string {
  const typeMap: Record<CertificateType, string> = {
    MILL_TEST_CERTIFICATE: 'Mill Test Certificate (MTC)',
    CERTIFICATE_OF_CONFORMANCE: 'Certificate of Conformance (CoC)',
    API_Q1: 'API Q1 Certification',
    API_Q2: 'API Q2 Certification',
    NACE_MR0175: 'NACE MR0175 / ISO 15156',
    ISO_CERTIFICATION: 'ISO Certification',
    MTR: 'Material Test Report',
    COC: 'Certificate of Compliance',
    COA: 'Certificate of Analysis',
    CALIBRATION: 'Calibration Certificate',
    SAFETY: 'Safety Certificate',
    ENVIRONMENTAL: 'Environmental Certificate',
    QUALITY: 'Quality Certificate',
    OTHER: 'Other',
  };
  return typeMap[type] || type;
}

export function getEN10204Description(subType: string): string {
  const descriptions: Record<string, string> = {
    '2.1': 'Supplier Declaration',
    '2.2': 'Test Report',
    '3.1': 'Manufacturer Certificate',
    '3.2': 'Third-Party Certificate',
  };
  return descriptions[subType] || '';
}

export function isExpiringSoon(expiryDate: string | undefined, days: number): boolean {
  if (!expiryDate) return false;
  const daysUntilExpiry = Math.ceil(
    (new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  return daysUntilExpiry > 0 && daysUntilExpiry <= days;
}

export function getDaysUntilExpiry(expiryDate: string | undefined): number {
  if (!expiryDate) return Infinity;
  return Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}
